import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, UserInfo } from '../services/authService';
import { setGlobalLogoutHandler, resetApiState } from '../services/api';
import { setDefaultTimezone } from '../utils/dateUtils';

export type User = UserInfo;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, totpToken?: string) => Promise<void>;
  /**
   * Store tokens and fetch the user, but DON'T activate the session yet — the
   * login page decides whether to prompt for 2FA before committing.
   */
  authenticate: (email: string, password: string, totpToken?: string) => Promise<User>;
  /** Activate the session, which swaps the app over to the authed routes. */
  commitSession: (userData: User) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    resetApiState();
    setDefaultTimezone(null);
    setUser(null);
  };

  useEffect(() => {
    // Register the logout handler so the API layer can end the session on 401.
    setGlobalLogoutHandler(logout);
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const userData = await authService.getCurrentUser();
        setDefaultTimezone(userData.timezone);
        setUser(userData);
      }
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const authenticate = async (email: string, password: string, totpToken?: string) => {
    const response = await authService.login(email, password, totpToken);
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('refreshToken', response.refresh_token);
    return await authService.getCurrentUser();
  };

  const commitSession = (userData: User) => {
    setDefaultTimezone(userData.timezone);
    setUser(userData);
  };

  const login = async (email: string, password: string, totpToken?: string) => {
    const userData = await authenticate(email, password, totpToken);
    commitSession(userData);
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setDefaultTimezone(userData.timezone);
      setUser(userData);
    } catch (error) {
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    authenticate,
    commitSession,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
